import React, { useEffect } from 'react';
import { IoIosClose } from 'react-icons/io';
import { Category, Filter, FilterOption } from '@interface';
import {
	residentialUnitConditions,
	generalConditionOptions,
	timeConditionOptions,
	dateConditionOptions,
	booleanOptions,
	programsList,
	filterOptionsByCategory,
} from '@/mockData/customSearch';

export const FilterComponent: React.FC<{
	category: Category;
	filters: Filter[];
	setFilters: React.Dispatch<React.SetStateAction<Filter[]>>;
	programSelectOpen: boolean;
	setProgramSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
	category,
	filters,
	setFilters,
	programSelectOpen,
	setProgramSelectOpen,
}) => {
	useEffect(() => {
		setFilters([]); // Clear all filters
		setProgramSelectOpen(false); // Close the program selection dropdown if needed
	}, [category]);

	const validateFilter = (filter: Filter): string | undefined => {
		// Check if value is empty
		if (typeof filter.value === 'number' && isNaN(filter.value)) {
			return 'Value cannot be empty';
		}

		// Check if number value is less than 1
		if (typeof filter.value === 'number' && filter.value < 1) {
			return 'Value must be at least 1';
		}

		// Special validation for 'small' category with Residential Units
		if (
			category === 'small' &&
			filter.type === 'residentialUnits' &&
			typeof filter.value === 'number' &&
			filter.value > 4
		) {
			return 'Small buildings must have 4 or fewer residential units';
		}

		// Check if between values are valid
		if (
			Array.isArray(filter.value) &&
			filter.condition === 'between' &&
			(isNaN(filter.value[0] as number) || isNaN(filter.value[1] as number))
		) {
			return 'Both values are required';
		}

		if (
			Array.isArray(filter.value) &&
			filter.condition === 'between' &&
			((filter.value[0] as number) < 1 || (filter.value[1] as number) < 1)
		) {
			return 'Values must be at least 1';
		}

		// Date validation
		if (filter.type === 'expiration') {
			if (filter.condition === 'between' && Array.isArray(filter.value)) {
				// Check if both dates are present for the 'between' condition
				if (!filter.value[0] || !filter.value[1]) {
					return 'Both dates are required';
				}
			} else if (!filter.value) {
				return 'Date cannot be empty';
			}
		}

		// For multiselect, check if any values are selected
		if (
			Array.isArray(filter.value) &&
			!filter.value.length &&
			filter.type === 'programs'
		) {
			return 'Please select at least one program';
		}

		return undefined;
	};

	const validateYear = (year: number | undefined): string | undefined => {
		if (year === undefined || isNaN(year)) {
			return 'Year cannot be empty';
		}

		if (year < 2007) {
			return 'Year cannot be less than 2007';
		}

		return undefined;
	};

	const removeFilter = (id: number) => {
		setFilters(filters.filter((filter) => filter.id !== id));
	};

	const addFilter = (option: FilterOption) => {
		const newFilter: Filter = {
			id: Date.now(),
			type: option.value,
			value:
				option.value === 'residentialUnits'
					? 1
					: option.isBoolean
						? true
						: option.isMultiSelect
							? []
							: option.hasDateCondition
								? option.value === 'expiration' && option.hasDateCondition
									? new Date()
									: new Date()
								: 1,
			condition:
				option.value === 'residentialUnits' && category === 'all'
					? 'atLeast'
					: option.value === 'expiration'
						? 'after'
						: 'exactly',
			year: option.hasTimeCondition ? 2007 : undefined,
			year2: option.hasTimeCondition ? 2007 : undefined,
		};

		setFilters([...filters, newFilter]);
	};

	const updateFilter = (id: number, key: keyof Filter, value: any) => {
		setFilters((prev: Filter[]) =>
			prev.map((f) => {
				if (f.id === id) {
					const updatedFilter = { ...f, [key]: value };

					// If updating the condition to 'between' for a date filter, initialize array value
					if (
						key === 'condition' &&
						value === 'between' &&
						updatedFilter.type === 'expiration'
					) {
						updatedFilter.value = [
							new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
							new Date(),
						];
					}

					// If switching from 'between' to single date condition
					if (
						key === 'condition' &&
						value !== 'between' &&
						updatedFilter.type === 'expiration' &&
						Array.isArray(updatedFilter.value)
					) {
						updatedFilter.value = new Date();
					}

					const error = validateFilter(updatedFilter);

					// Validate year if it's being updated or if this filter has a year
					let yearError;
					let yearError2;
					if (key === 'year' || updatedFilter.year !== undefined) {
						yearError = validateYear(updatedFilter.year);
					}

					if (key === 'year2' || updatedFilter.year2 !== undefined) {
						yearError2 = validateYear(updatedFilter.year2);
					}

					return { ...updatedFilter, error, yearError, yearError2 };
				}
				return f;
			})
		);
	};

	const updateDateInRange = (id: number, index: 0 | 1, date: Date) => {
		setFilters((prev: Filter[]) =>
			prev.map((f) => {
				if (f.id === id && Array.isArray(f.value)) {
					const newDates = [...f.value] as [Date, Date];
					newDates[index] = date;
					const updatedFilter = { ...f, value: newDates };
					const error = validateFilter(updatedFilter);
					return { ...updatedFilter, error };
				}
				return f;
			})
		);
	};

	const removeProgram = (filterId: number, programToRemove: string) => {
		setFilters((prev: Filter[]) =>
			prev.map((filter) => {
				if (filter.id === filterId && Array.isArray(filter.value)) {
					const updatedFilter = {
						...filter,
						value: (filter.value as string[]).filter(
							(prog: string) => prog !== programToRemove
						),
					};
					const error = validateFilter(updatedFilter);
					return { ...updatedFilter, error };
				}
				return filter;
			})
		);
	};

	const addProgram = (filterId: number, program: string) => {
		setFilters((prev: Filter[]) =>
			prev.map((filter) => {
				if (filter.id === filterId && Array.isArray(filter.value)) {
					const updatedFilter = {
						...filter,
						value: [...filter.value, program] as string[],
					};
					const error = validateFilter(updatedFilter);
					return { ...updatedFilter, error };
				}
				return filter;
			})
		);
	};

	const isProgramsValue = (value: Filter['value']): value is string[] => {
		return Array.isArray(value) && typeof value[0] === 'string';
	};

	const handleBlur = (
		id: number,
		field?: 'value' | 'year' | 'year2' | 'date'
	) => {
		setFilters((prev: Filter[]) =>
			prev.map((f) => {
				if (f.id === id) {
					if (field === 'year') {
						const yearError = validateYear(f.year);
						return { ...f, yearError };
					} else if (field === 'year2') {
						const yearError2 = validateYear(f.year2);
						return { ...f, yearError2 };
					} else {
						const error = validateFilter(f);
						return { ...f, error };
					}
				}
				return f;
			})
		);
	};

	return (
		<div className="p-4 mx-auto w-full">
			<div className="mt-4 space-y-3">
				{filters.map((filter) => {
					const option = filterOptionsByCategory[category].find(
						(o) => o.value === filter.type
					);
					if (!option) return null;

					const conditionList =
						filter.type === 'residentialUnits' && category === 'all'
							? residentialUnitConditions
							: generalConditionOptions;
					const isBetween = filter.condition === 'between';

					return (
						<div key={filter.id} className="flex flex-col w-full">
							<div className="flex items-center space-x-2">
								<span className="text-[14px] font-semibold">
									{option.label}:
								</span>

								{option.isBoolean ? (
									<select
										className="bg-transparent focus:outline-none border-b pb-1"
										value={filter.value.toString()}
										onChange={(e) =>
											updateFilter(
												filter.id,
												'value',
												e.target.value === 'true'
											)
										}
									>
										{booleanOptions.map((opt) => (
											<option
												key={opt.value.toString()}
												value={opt.value.toString()}
											>
												{opt.label}
											</option>
										))}
									</select>
								) : option.isMultiSelect ? (
									<div className="flex flex-col w-full md:w-1/2">
										<div className="flex flex-wrap gap-2 min-h-[30px] p-2 border rounded-md">
											{isProgramsValue(filter.value) &&
												filter.value.map((program) => (
													<div
														key={program}
														className="flex items-center bg-gray-100 rounded px-2 py-1 text-sm"
													>
														<span>{program}</span>
														<button
															onClick={() => removeProgram(filter.id, program)}
															className="ml-2 text-gray-500 hover:text-gray-700"
														>
															<IoIosClose className="h-4 w-4" />
														</button>
													</div>
												))}
										</div>
										<div className="relative mt-2">
											<button
												onClick={() => setProgramSelectOpen(!programSelectOpen)}
												className="text-blue-600 hover:underline text-sm"
											>
												Select Programs
											</button>
											{programSelectOpen && (
												<div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
													{programsList
														.filter(
															(program) =>
																!isProgramsValue(filter.value) ||
																!filter.value.includes(program)
														)
														.map((program) => (
															<button
																key={program}
																className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
																onClick={() => {
																	addProgram(filter.id, program);
																	setProgramSelectOpen(false);
																}}
															>
																{program}
															</button>
														))}
												</div>
											)}
										</div>
									</div>
								) : option.hasDateCondition ? (
									<div className="flex flex-wrap items-center space-x-2">
										<select
											className="bg-transparent focus:outline-none border-b pb-1"
											value={filter.condition}
											onChange={(e) =>
												updateFilter(filter.id, 'condition', e.target.value)
											}
										>
											{dateConditionOptions.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>

										{filter.condition === 'between' ? (
											<>
												<input
													type="date"
													value={
														Array.isArray(filter.value)
															? (filter.value[0] as Date)
																	.toISOString()
																	.slice(0, 10)
															: new Date().toISOString().slice(0, 10)
													}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														e.target.value &&
														updateDateInRange(
															filter.id,
															0,
															new Date(e.target.value)
														)
													}
													className={`bg-transparent focus:outline-none border-b pb-1 ${
														filter.error ? 'border-red-500' : ''
													}`}
													onBlur={() => handleBlur(filter.id, 'date')}
												/>
												<span>and</span>

												<input
													type="date"
													value={
														Array.isArray(filter.value)
															? (filter.value[1] as Date)
																	.toISOString()
																	.slice(0, 10)
															: new Date().toISOString().slice(0, 10)
													}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														e.target.value &&
														updateDateInRange(
															filter.id,
															1,
															new Date(e.target.value)
														)
													}
													className={`bg-transparent focus:outline-none border-b pb-1 ${
														filter.error ? 'border-red-500' : ''
													}`}
													onBlur={() => handleBlur(filter.id, 'date')}
												/>
											</>
										) : (
											<input
												type="date"
												value={
													filter.value as Date
														? (filter.value as Date)
																.toISOString()
																.slice(0, 10)
														: new Date().toISOString().slice(0, 10)
												}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													e.target.value &&
													updateFilter(
														filter.id,
														'value',
														new Date(e.target.value)
													)
												}
												className={`bg-transparent focus:outline-none border-b pb-1 ${
													filter.error ? 'border-red-500' : ''
												}`}
												onBlur={() => handleBlur(filter.id, 'date')}
											/>
										)}
									</div>
								) : option.hasTimeCondition ? (
									<div className="flex items-center space-x-2">
										<select
											className="bg-transparent focus:outline-none border-b pb-1"
											value={filter.condition}
											onChange={(e) =>
												updateFilter(filter.id, 'condition', e.target.value)
											}
										>
											{conditionList.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
										<input
											type="number"
											className={`w-16 bg-transparent focus:outline-none border-b pb-1 ${
												filter.error ? 'border-red-500' : ''
											}`}
											value={filter.value as number}
											onChange={(e) =>
												updateFilter(
													filter.id,
													'value',
													e.target.value === '' ? NaN : parseInt(e.target.value)
												)
											}
											onBlur={() => handleBlur(filter.id, 'value')}
										/>
										<span>%</span>
										<select
											className="bg-transparent focus:outline-none border-b pb-1"
											value={filter.timeCondition}
											onChange={(e) =>
												updateFilter(filter.id, 'timeCondition', e.target.value)
											}
										>
											{timeConditionOptions.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
										<input
											type="number"
											className={`w-16 bg-transparent focus:outline-none border-b pb-1 ${
												filter.yearError ? 'border-red-500' : ''
											}`}
											placeholder="Year"
											value={filter.year !== undefined ? filter.year : ''}
											onChange={(e) =>
												updateFilter(
													filter.id,
													'year',
													e.target.value === '' ? NaN : parseInt(e.target.value)
												)
											}
											onBlur={() => handleBlur(filter.id, 'year')}
										/>

										{filter.timeCondition === 'between' && (
											<>
												<span>and</span>
												<input
													type="number"
													className={`w-16 bg-transparent focus:outline-none border-b pb-1 ${
														filter.yearError2 ? 'border-red-500' : ''
													}`}
													placeholder="Year"
													value={filter.year2 !== undefined ? filter.year2 : ''}
													onChange={(e) =>
														updateFilter(
															filter.id,
															'year2',
															e.target.value === ''
																? NaN
																: parseInt(e.target.value)
														)
													}
													onBlur={() => handleBlur(filter.id, 'year2')}
												/>
											</>
										)}
									</div>
								) : (
									<div className="flex items-center space-x-2">
										<select
											className="bg-transparent focus:outline-none border-b pb-1"
											value={filter.condition}
											onChange={(e) =>
												updateFilter(filter.id, 'condition', e.target.value)
											}
										>
											{conditionList.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
										{isBetween ? (
											<>
												<input
													type="number"
													className={`w-16 bg-transparent focus:outline-none border-b pb-1 ${
														filter.error ? 'border-red-500' : ''
													}`}
													placeholder="Min"
													value={
														Array.isArray(filter.value) &&
														!isNaN(filter.value[0] as number)
															? (filter.value[0] as number)
															: 0
													}
													onChange={(e) =>
														updateFilter(filter.id, 'value', [
															e.target.value === ''
																? NaN
																: parseInt(e.target.value),
															Array.isArray(filter.value) ? filter.value[1] : 0,
														])
													}
													onBlur={() => handleBlur(filter.id, 'value')}
												/>
												<span>and</span>
												<input
													type="number"
													className={`w-16 bg-transparent focus:outline-none border-b pb-1 ${
														filter.error ? 'border-red-500' : ''
													}`}
													placeholder="Max"
													value={
														Array.isArray(filter.value) &&
														!isNaN(filter.value[1] as number)
															? (filter.value[1] as number)
															: 0
													}
													onChange={(e) =>
														updateFilter(filter.id, 'value', [
															Array.isArray(filter.value) ? filter.value[0] : 0,
															e.target.value === ''
																? NaN
																: parseInt(e.target.value),
														])
													}
													onBlur={() => handleBlur(filter.id, 'value')}
												/>
											</>
										) : (
											<input
												type="number"
												className={`w-16 bg-transparent focus:outline-none border-b pb-1 ${
													filter.error ? 'border-red-500' : ''
												}`}
												value={
													typeof filter.value === 'number' &&
													!isNaN(filter.value)
														? filter.value
														: ''
												}
												onChange={(e) =>
													updateFilter(
														filter.id,
														'value',
														e.target.value === ''
															? NaN
															: parseInt(e.target.value)
													)
												}
												onBlur={() => handleBlur(filter.id, 'value')}
											/>
										)}
										<span>units</span>
									</div>
								)}

								<button
									onClick={() => removeFilter(filter.id)}
									className="text-gray-500 hover:text-gray-700"
								>
									<IoIosClose className="h-[25px] w-[25px]" />
								</button>
							</div>
							{filter.error && (
								<div className="text-red-500 text-sm mt-1 ml-[120px]">
									{filter.error}
								</div>
							)}
							{filter.yearError && (
								<div className="text-red-500 text-sm mt-1 ml-[120px]">
									{filter.yearError}
								</div>
							)}

							{filter.yearError2 && (
								<div className="text-red-500 text-sm mt-1 ml-[120px]">
									{filter.yearError2}
								</div>
							)}
						</div>
					);
				})}
			</div>
			<div className="mt-4">
				<h3 className="font-bold text-[14px] mb-2">Options</h3>
				<div className="flex flex-col space-y-2">
					{filterOptionsByCategory[category]
						.filter((option) => !filters.some((f) => f.type === option.value))
						.map((option) => (
							<button
								key={option.value}
								className="text-blue-600 hover:underline text-left text-[14px]"
								onClick={() => addFilter(option)}
							>
								{option.label}
							</button>
						))}
				</div>
			</div>
		</div>
	);
};
